import { useMemo } from 'react';
import {
  ApiOutlined,
  AreaChartOutlined,
  DatabaseOutlined,
  DollarOutlined,
  InboxOutlined,
  LineChartOutlined,
  LoginOutlined,
  LogoutOutlined,
  NodeIndexOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { Bento, StatTile, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import {
  BieuDoCot,
  BieuDoXepChong,
  ChuGiai,
  MAU_AM,
  MAU_DUONG,
  MAU_TIEN,
  MAU_TOKEN,
  type CotDon,
  type CotXepChong,
} from '../../chung/components/BieuDo';
import { DauChatLuong } from '../../chung/components/DauChatLuong';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { useThongKeAi } from '../../chung/hooks/useThongKe';
import type { LocAi } from '../../chung/nghiep-vu/loc-ai';
import { TRONG, doTien, nhanMoc, nhanMocDayDu, soNguyen, tyLe } from '../../chung/nghiep-vu/so-thong-ke';
import { soTien } from '../../chung/nghiep-vu/so-tien';
import { LOAI_TOKEN, type NhomPhanRa, type ThongKeAi } from '../../chung/nghiep-vu/types-thong-ke';
import { useNguonVet } from '../nguon';
import { kiemBayLoai } from '../nghiep-vu/vet';
import { BangPhanRa } from './BangPhanRa';

/**
 * Tab "Tổng quan" của màn Sử dụng AI — mục 7.11 và 8.2. Port `thong-ke/TongQuanAi.tsx` native.
 *
 * Khoảng thời gian thuộc về khung tab (một khoảng cho cả bốn tab), nên tab này KHÔNG có ô chọn thời gian và không tự
 * lấy `Date.now()`.
 *
 * ── TENANT CHỈ THẤY TOKEN FOX (ADR-0123 mục 1, 5) ───────────────────────
 * Cổng tenant: bốn ô số và biểu đồ token Fox đầu vào, đầu ra, rồi bảng phân rã. Cổng nền tảng thêm token thật, bảy lát,
 * giá vốn, tiết kiệm nhờ đệm và dải chất lượng số liệu; mọi phần ấy nằm ở `PhanNenTang`.
 *
 * ── BẢY LÁT PHẢI CỘNG ĐỦ TỔNG, VÀ NGƯỜI ĐỌC PHẢI KIỂM ĐƯỢC ───────────────
 * Bảy loại token là một PHÂN HOẠCH (QĐ-14). Cổng nền tảng kiểm bằng mắt ở ba chỗ: cột xếp chồng vẽ theo `tong_token` (lệch
 * thì hở một khe ở đỉnh), chú giải in con số từng lát kèm tỷ lệ, và một dòng dưới biểu đồ đặt "tổng bảy lát" cạnh "tổng do
 * server chốt". Lệch thì dòng ấy đỏ lên — đó là bộ chuẩn hoá token đang sai, không phải lỗi hiển thị cần che.
 */
export function TongQuanAi({
  tu,
  den,
  loc,
  coLoc = false,
  nhanKhoa,
}: {
  tu: string;
  den: string;
  loc?: LocAi;
  /** Đang có ô lọc — rỗng thì chỉ nói không có gì khớp, không gợi ý mở rộng khoảng. */
  coLoc?: boolean;
  nhanKhoa?: (nhom: NhomPhanRa, khoa: string) => string;
}) {
  const { t, locale } = useT();
  const truyVan = useThongKeAi(tu, den, loc);
  const du = truyVan.data;

  const { nenTang } = useNguonVet();

  const nhanFox = useMemo(
    () => [t('nktvSuDungAi.foxVao'), t('nktvSuDungAi.foxRa')],
    // `t` là hàm mới mỗi lần vẽ; nhãn chỉ đổi khi đổi ngôn ngữ.
    [locale],
  );

  const cotFox = useMemo<CotXepChong[]>(() => {
    if (!du) return [];
    return du.theo_thoi_gian.map((hang) => {
      const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
      return {
        nhan,
        tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
        // `null` là dòng cũ chưa có token Fox: không có gì để vẽ, cột thấp đi chứ không bịa số.
        lat: [hang.fox_dau_vao ?? 0, hang.fox_dau_ra ?? 0],
        tong: hang.fox_tokens ?? 0,
        moTa: `${nhan} — ${nhanFox[0]} ${soNguyen(hang.fox_dau_vao, locale)} · ${nhanFox[1]} ${soNguyen(hang.fox_dau_ra, locale)}`,
      };
    });
  }, [du, nhanFox, locale]);

  if (truyVan.isPending) {
    return (
      // Khung giữ chỗ nằm trong ô `surface` như bản có dữ liệu: `Skeleton` tô `bg-subtle`, đặt thẳng trên nền trang
      // (cũng `bg-subtle`) thì không thấy gì — trông như màn trắng chứ không như đang tải.
      <div className="space-y-4" aria-busy>
        <Tile className="p-4">
          <Skeleton className="h-16" />
        </Tile>
        <Tile className="p-4">
          <Skeleton className="h-60" />
        </Tile>
        <Tile className="p-4">
          <Skeleton className="h-60" />
        </Tile>
      </div>
    );
  }

  if (truyVan.isError || du === undefined) {
    return (
      <Tile>
        <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
      </Tile>
    );
  }

  const tong = du.tong;

  if (tong.so_luot === 0) {
    return (
      <Tile>
        {coLoc ? (
          <Empty icon={InboxOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
        ) : (
          <Empty
            icon={InboxOutlined}
            title={t('nktvSuDungAi.tongQuan.khongLuotTieuDe')}
            description={t('nktvSuDungAi.tongQuan.khongLuotMoTa')}
          />
        )}
      </Tile>
    );
  }

  return (
    <div className="space-y-4">
      {nenTang && (
        <DauChatLuong
          // Không có `AI_PRICING.READ` thì không có giá vốn để nói là thiếu.
          soChuaCoGia={tong.chi_phi_von !== undefined ? (tong.so_chua_co_gia ?? 0) : 0}
          soThieuTyGia={tong.chi_phi_von !== undefined ? tong.so_thieu_ty_gia : 0}
          soUocTinh={tong.so_uoc_tinh ?? 0}
          soCoCanhBao={tong.so_co_canh_bao ?? 0}
          soLuot={tong.so_luot}
        />
      )}

      <Bento>
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.soLuot')}
          so={soNguyen(tong.so_luot, locale)}
          sub={t('nktvSuDungAi.tongQuan.soLuotPhu', { trace: soNguyen(tong.so_trace, locale) })}
          icon={<ThunderboltOutlined />}
          tone="sky"
        />
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.tokenFox')}
          so={soNguyen(tong.fox_tokens, locale)}
          sub={t('nktvSuDungAi.tongQuan.tokenFoxPhu')}
          icon={<DatabaseOutlined />}
          tone="teal"
        />
        {nenTang ? (
          <>
            <StatTile
              nhan={t('nktvSuDungAi.cotTokenThat')}
              so={soNguyen(tong.tong_token, locale)}
              sub={t('nktvSuDungAi.tongQuan.tokenThatPhu')}
              icon={<ApiOutlined />}
              tone="violet"
            />
            {tong.chi_phi_von !== undefined ? (
              <StatTile
                nhan={t('nktvSuDungAi.cotGiaVon')}
                so={soTien(tong.chi_phi_von, tong.don_vi_tien ?? null, locale) ?? TRONG}
                sub={
                  (tong.so_chua_co_gia ?? 0) > 0
                    ? t('nktvSuDungAi.tongQuan.giaVonChuaDu', { so: soNguyen(tong.so_chua_co_gia, locale) })
                    : t('nktvSuDungAi.tongQuan.giaVonPhu')
                }
                icon={<DollarOutlined />}
                tone={(tong.so_chua_co_gia ?? 0) > 0 ? 'warning' : 'primary'}
              />
            ) : (
              <StatTile
                nhan={t('nktvSuDungAi.tongQuan.soMoHinh')}
                so={soNguyen(tong.so_mo_hinh, locale)}
                sub={t('nktvSuDungAi.tongQuan.soMoHinhPhu')}
                icon={<ApiOutlined />}
                tone="neutral"
              />
            )}
          </>
        ) : (
          <>
            <StatTile
              nhan={t('nktvSuDungAi.tongQuan.foxVao')}
              so={soNguyen(tong.fox_dau_vao, locale)}
              sub={t('nktvSuDungAi.tongQuan.tyLeTrenTong', { tyLe: tyLe(tong.fox_dau_vao ?? 0, tong.fox_tokens ?? 0, locale) })}
              icon={<LoginOutlined />}
              tone="sky"
            />
            <StatTile
              nhan={t('nktvSuDungAi.tongQuan.foxRa')}
              so={soNguyen(tong.fox_dau_ra, locale)}
              sub={t('nktvSuDungAi.tongQuan.tyLeTrenTong', { tyLe: tyLe(tong.fox_dau_ra ?? 0, tong.fox_tokens ?? 0, locale) })}
              icon={<LogoutOutlined />}
              tone="primary"
            />
          </>
        )}
      </Bento>

      <Tile>
        <TileHeader icon={<AreaChartOutlined />} tone="teal" title={t('nktvSuDungAi.bieuDo.foxTieuDe')} />
        <TileBody>
          <BieuDoXepChong
            cot={cotFox}
            loat={[
              { nhan: nhanFox[0] ?? '', mau: MAU_TOKEN.dau_vao },
              { nhan: nhanFox[1] ?? '', mau: MAU_TOKEN.dau_ra },
            ]}
            nhan={t('nktvSuDungAi.bieuDo.foxNhan')}
            nhanTong={t('nktvSuDungAi.bieuDo.foxTong')}
            dinhDang={(gt) => t('nktvSuDungAi.bieuDo.soTokenThe', { so: soNguyen(gt, locale) })}
          />
          <ChuGiai
            muc={[
              {
                nhan: nhanFox[0] ?? '',
                mau: MAU_TOKEN.dau_vao,
                phu: `${soNguyen(tong.fox_dau_vao, locale)} · ${tyLe(tong.fox_dau_vao ?? 0, tong.fox_tokens ?? 0, locale)}`,
              },
              {
                nhan: nhanFox[1] ?? '',
                mau: MAU_TOKEN.dau_ra,
                phu: `${soNguyen(tong.fox_dau_ra, locale)} · ${tyLe(tong.fox_dau_ra ?? 0, tong.fox_tokens ?? 0, locale)}`,
              },
            ]}
          />
        </TileBody>
      </Tile>

      {nenTang && <PhanNenTang du={du} />}

      <BangPhanRa thongKe={du} nhanKhoa={nhanKhoa} />
    </div>
  );
}

/** Token thật bảy loại, giá vốn và tiết kiệm nhờ đệm theo thời gian — chỉ cổng nền tảng. */
function PhanNenTang({ du }: { du: ThongKeAi }) {
  const { t, locale } = useT();
  const tong = du.tong;

  const nhanLoai = LOAI_TOKEN.map((loai) => t(`nktv.loaiToken.${loai}`));
  const cotToken: CotXepChong[] = du.theo_thoi_gian.map((hang) => {
    const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
    const lat = LOAI_TOKEN.map((loai) => hang[`token_${loai}`] ?? 0);
    const chiTiet = lat.map((gt, i) => `${nhanLoai[i] ?? ''} ${soNguyen(gt, locale)}`).join(' · ');
    return {
      nhan,
      tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
      lat,
      tong: hang.tong_token ?? 0,
      moTa: `${nhan} — ${t('nktvSuDungAi.bieuDo.tongToken')} ${soNguyen(hang.tong_token, locale)} · ${chiTiet}`,
    };
  });

  const cotTien = (doc: (h: ThongKeAi['theo_thoi_gian'][number]) => string | undefined, nhanThe: string, duongAm: boolean): CotDon[] =>
    du.theo_thoi_gian.map((hang) => {
      const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
      const gt = doTien(doc(hang));
      const tien = soTien(gt, hang.don_vi_tien ?? null, locale) ?? TRONG;
      return {
        nhan,
        tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
        giaTri: gt ?? 0,
        moTa: `${nhan} — ${tien} · ${t('nktvSuDungAi.bieuDo.soLuot')} ${soNguyen(hang.so_luot, locale)}`,
        the: [
          { nhan: nhanThe, giaTri: tien, mau: duongAm ? ((gt ?? 0) < 0 ? MAU_AM : MAU_DUONG) : MAU_TIEN },
          { nhan: t('nktvSuDungAi.bieuDo.soLuot'), giaTri: soNguyen(hang.so_luot, locale) },
        ],
      };
    });

  const canhToken = kiemBayLoai(du);
  const thongSoCanh = {
    bay: soNguyen(canhToken.bay, locale),
    tong: soNguyen(tong.tong_token, locale),
  };
  const coGiaVon = tong.chi_phi_von !== undefined;
  const coTietKiem = tong.tiet_kiem_dem !== undefined;
  const tienTietKiem = doTien(tong.tiet_kiem_dem);
  const amTietKiem = tienTietKiem !== null && tienTietKiem < 0;
  // Có một khối thì khối đó chiếm đủ hàng.
  const nuaHang = coGiaVon && coTietKiem ? 'lg:col-span-6' : undefined;

  return (
    <>
      <Tile>
        <TileHeader
          icon={<AreaChartOutlined />}
          tone="violet"
          title={t('nktvSuDungAi.bieuDo.tokenTieuDe')}
          mo={t('nktvSuDungAi.bieuDo.tokenMoTa')}
        />
        <TileBody>
          <BieuDoXepChong
            cot={cotToken}
            loat={LOAI_TOKEN.map((loai, i) => ({ nhan: nhanLoai[i] ?? loai, mau: MAU_TOKEN[loai] }))}
            nhan={t('nktvSuDungAi.bieuDo.tokenNhan')}
            nhanTong={t('nktvSuDungAi.bieuDo.tongToken')}
            dinhDang={(gt) => t('nktvSuDungAi.bieuDo.soTokenThe', { so: soNguyen(gt, locale) })}
          />
          <ChuGiai
            muc={LOAI_TOKEN.map((loai, i) => ({
              nhan: nhanLoai[i] ?? loai,
              mau: MAU_TOKEN[loai],
              phu: `${soNguyen(tong[`token_${loai}`], locale)} · ${tyLe(tong[`token_${loai}`] ?? 0, tong.tong_token ?? 0, locale)}`,
              moTa: t(`nktv.loaiTokenMoTa.${loai}`),
            }))}
          />
          <p
            className={
              canhToken.khop
                ? 'mt-3 text-xs leading-relaxed text-content-muted'
                : 'mt-3 text-xs font-medium leading-relaxed text-danger-700'
            }
            role={canhToken.khop ? undefined : 'alert'}
          >
            {canhToken.khop
              ? t('nktvSuDungAi.bieuDo.tokenKhop', thongSoCanh)
              : t('nktvSuDungAi.bieuDo.tokenLech', { ...thongSoCanh, moc: soNguyen(canhToken.soMocLech, locale) })}
          </p>
        </TileBody>
      </Tile>

      {(coGiaVon || coTietKiem) && (
        <Bento>
          {coGiaVon && (
            <Tile span={nuaHang}>
              <TileHeader
                icon={<LineChartOutlined />}
                tone="primary"
                title={t('nktvSuDungAi.bieuDo.giaVonTieuDe')}
                mo={t('nktvSuDungAi.bieuDo.giaVonMoTa')}
              />
              <TileBody>
                <BieuDoCot
                  cot={cotTien((h) => h.chi_phi_von, t('nktvSuDungAi.cotGiaVon'), false)}
                  nhan={t('nktvSuDungAi.bieuDo.giaVonNhan')}
                  mau={MAU_TIEN}
                />
                {(tong.so_chua_co_gia ?? 0) > 0 && (
                  <p className="mt-3 text-xs leading-relaxed text-content-muted">
                    {t('nktvSuDungAi.bieuDo.giaVonChuaDu', { so: soNguyen(tong.so_chua_co_gia, locale) })}
                  </p>
                )}
              </TileBody>
            </Tile>
          )}

          {coTietKiem && (
            <Tile span={nuaHang}>
              <TileHeader
                icon={<NodeIndexOutlined />}
                tone={amTietKiem ? 'danger' : 'success'}
                title={t('nktvSuDungAi.bieuDo.tietKiemTieuDe')}
                mo={t('nktvSuDungAi.bieuDo.tietKiemMoTa')}
              />
              <TileBody>
                <p
                  className={
                    amTietKiem
                      ? 'text-xl font-semibold tabular-nums text-danger-700'
                      : 'text-xl font-semibold tabular-nums text-success-700'
                  }
                >
                  {soTien(tienTietKiem, tong.don_vi_tien ?? null, locale) ?? TRONG}
                </p>
                <BieuDoCot
                  cot={cotTien((h) => h.tiet_kiem_dem, t('nktvSuDungAi.bieuDo.tietKiemThe'), true)}
                  nhan={t('nktvSuDungAi.bieuDo.tietKiemNhan')}
                  mau={MAU_DUONG}
                  mauAm={MAU_AM}
                  cao={150}
                />
                <p className="mt-3 text-xs leading-relaxed text-content-muted">{t('nktvSuDungAi.bieuDo.tietKiemAm')}</p>
                <p className="mt-1 text-xs leading-relaxed text-content-muted">
                  {t('nktvSuDungAi.bieuDo.tietKiemPhiLuu')}
                </p>
              </TileBody>
            </Tile>
          )}
        </Bento>
      )}
    </>
  );
}
